// « Le chat de l’étage »: a grey and white cat living between the teams of a floor (floor.cat ===
// true). Pure behaviour, no three.js import (unit-tested in Node); cat-view.js draws it. Decoration
// only: it lives in the browser, nothing on the server, no AI call.
//
// The cat sits, lies down for long naps, washes a paw, looks around, stretches when it gets up from
// a nap and walks quietly from team to team along straight segments that cross no pod, walkway or
// hall (floor-area.js), keeping clear of the floor's chicken. It is still most of the time. Pacing
// (pacing.js): while it moves or settles into a posture it asks for frames at the agents' working
// rate; still, a hidden page, the building view, the elevator ride and reduced motion ask for none.
// The step functions only mutate the state object: nothing is allocated per frame.

import { rng } from './layout.js';
import { SCENE_PACING } from './pacing.js';
import { damp, dampAngle, wrapAngle, clamp, lerp } from './anim.js';
import { floorArea, isFree, nearTeam, segmentClear, segmentDistance, startPoint } from './floor-area.js';

/** Behaviour settings (world units, seconds, radians). */
export const CAT = Object.freeze({
  radius: 0.34,        // keep-out around pods, walkways and the hall (half a cat + room)
  edge: 0.9,           // how far beyond the floor's content (pods, hall) it may wander
  near: 2.2,           // targets stay this close to a team (a pod), so it lives among them
  speed: 0.4,          // walking speed
  turnRate: 6,         // yaw damping toward the heading (1/s)
  stride: 0.1,         // distance per half gait cycle
  walkMin: 1,
  walkMax: 3.4,
  walkTimeout: 16,     // a walk that takes longer than this ends (never stuck)
  sitMin: 8,           // sitting still
  sitMax: 18,
  napMin: 22,          // lying down: a nap
  napMax: 45,
  standMin: 1.5,       // a short pause on its feet
  standMax: 3.5,
  firstRestMin: 1.5,   // a cat that just appeared starts moving soon
  firstRestMax: 4,
  retryRest: 2,        // short rest when no reachable target was found
  groomMin: 2.6,       // washing a paw
  groomMax: 4.2,
  lookMin: 2.2,        // looking around
  lookMax: 3.4,
  stretchTime: 2.4,
  settleRate: 6,       // into a posture: sitting down, lying down, getting up
  avoid: 0.75,         // keeps this far from the floor's chicken (env.avoid)
  tries: 24,           // random targets tried before giving up
  wakeSlack: 0.03,     // a rest that ends within this is over (timer jitter)
});

/**
 * The joints of the cat's still postures (cat-view.js, model units and radians): the torso's offset
 * from its standing pivot (hipY, hipZ) and its pitch (negative: nose up); the legs' swing from the
 * torso (fl, fr, bl, br; negative: paw forward) and the front legs' reach (scale); the head's nod
 * (positive: nose down) and turn (positive: to its left); the tail's lift (positive: up) in its
 * heading, turned by tailSway (positive: to its right), then its curved tip: curl, turn (wrap) and
 * roll (-π/2: the curve bends up, 0: sideways).
 */
export const CAT_POSES = Object.freeze({
  stand: Object.freeze({
    hipY: 0, hipZ: 0, pitch: 0, fl: 0, fr: 0, bl: 0, br: 0, reach: 1,
    headPitch: 0.06, headYaw: 0, tailLift: -0.25, tailSway: 0, tailCurl: 0, tailWrap: 0, tailRoll: -1.57,
  }),
  // Upright on its haunches, the hind paws forward under it, its tail curled around its side.
  sit: Object.freeze({
    hipY: -0.116, hipZ: -0.02, pitch: -0.65, fl: 0.65, fr: 0.65, bl: -0.62, br: -0.62, reach: 1.18,
    headPitch: 0.6, headYaw: 0, tailLift: -0.5, tailSway: 1.8, tailCurl: 0, tailWrap: 0.1, tailRoll: 0.8,
  }),
  // Lying like a sphinx, the front paws out, the hind legs tucked, its tail along its side.
  lie: Object.freeze({
    hipY: -0.13, hipZ: -0.01, pitch: -0.02, fl: -1.32, fr: -1.32, bl: -1.32, br: -1.32, reach: 1,
    headPitch: 0.2, headYaw: 0, tailLift: -0.55, tailSway: 1.7, tailCurl: 0, tailWrap: 0, tailRoll: -0.25,
  }),
});

/** The deepest point of the stretch (front low, paws far forward, rear up, head raised, tail up). */
const STRETCH = Object.freeze({
  hipY: 0, hipZ: 0.02, pitch: 0.3, fl: -1.26, fr: -1.26, bl: -0.3, br: -0.3, reach: 1,
  headPitch: -0.42, headYaw: 0, tailLift: 1.1, tailSway: 0, tailCurl: 0, tailWrap: 0, tailRoll: -1.57,
});

/** The raised right paw of the grooming cat, licked (from the sitting posture). */
const GROOM = Object.freeze({ fr: -1.72, headPitch: 0.8, headYaw: -0.32, reach: 0.94 });

/** The joints (CAT_POSES keys) the state carries. */
export const CAT_JOINTS = Object.freeze(Object.keys(CAT_POSES.stand));

// The pose a wash or a look aims at this frame (filled in place: nothing allocated per frame).
const aim = { ...CAT_POSES.sit };

const NO_ENV = Object.freeze({});
const TWO_PI = Math.PI * 2;

/**
 * Floor ids whose cat is on (only a boolean true counts).
 * @param {{floors?:Array<{id?:string, cat?:unknown}>}} office
 * @returns {Set<string>}
 */
export function catFloorIds(office) {
  const ids = new Set();
  for (const f of office?.floors || []) if (f && f.id && f.cat === true) ids.add(f.id);
  return ids;
}

/**
 * Where the cat may walk on a floor (floor-area.js floorArea).
 * @param {object} layout the floor layout (layout.js floorLayout)
 * @param {{margin?:number, edge?:number, near?:number}} [opts]
 */
export function catArea(layout, opts = {}) {
  return floorArea(layout, {
    margin: opts.margin ?? CAT.radius, edge: opts.edge ?? CAT.edge, near: opts.near ?? CAT.near,
  });
}

function setJoints(s, pose) {
  for (let i = 0; i < CAT_JOINTS.length; i++) s[CAT_JOINTS[i]] = pose[CAT_JOINTS[i]];
}

/**
 * A new cat, sitting or lying at a random free point (or at `at` when it is free: the floor was
 * rebuilt), already in its posture (it asks for no frame).
 * @param {{area:ReturnType<typeof catArea>, seed?:number, now?:number,
 *   at?:{x:number, z:number, yaw?:number}|null}} opts now: wall clock (s)
 */
export function createCat({ area, seed = 1, now = 0, at = null }) {
  const rand = rng(seed);
  let p = startPoint(area, rand);
  let yaw = rand() * TWO_PI - Math.PI;
  if (at && Number.isFinite(at.x) && Number.isFinite(at.z) && isFree(area, at.x, at.z)) {
    p = { x: at.x, z: at.z };
    if (Number.isFinite(at.yaw)) yaw = wrapAngle(at.yaw);
  }
  const posture = rand() < 0.6 ? 'sit' : 'lie';
  const s = {
    area, rand,
    x: p.x, z: p.z, yaw,
    mode: 'rest',
    posture,
    until: now + lerp(CAT.firstRestMin, CAT.firstRestMax, rand()),
    posed: true,
    // Walk (deadline: wall clock).
    tx: p.x, tz: p.z, speed: 0, gait: 0, amp: 0, deadline: 0,
    // Groom / look / stretch progress (s), length (s) and side (±1).
    k: 0, dur: 0, side: 1,
    // Walking bob of the body (model units), on top of the joints.
    bob: 0,
  };
  setJoints(s, CAT_POSES[posture]);
  return s;
}

/** True while the cat moves (or settles into its posture): it needs frames. */
export function wantsFrames(s) {
  return !!s && (s.mode !== 'rest' || !s.posed);
}

function frozen(env) {
  return !!(env.hidden || env.building || env.riding || env.reducedMotion);
}

/**
 * Frame rate the cat asks for: the agents' working rate while it moves, 0 otherwise.
 * @param {object|null} s cat state
 * @param {{hidden?:boolean, building?:boolean, riding?:boolean, reducedMotion?:boolean}} [env]
 * @param {typeof SCENE_PACING} [pacing]
 * @returns {number}
 */
export function catFps(s, env = NO_ENV, pacing = SCENE_PACING) {
  if (!s || frozen(env)) return 0;
  return wantsFrames(s) ? pacing.activeFps : 0;
}

/**
 * Wall-clock time (s) at which a resting cat wants its next frame (it gets up or changes posture);
 * Infinity when it needs no wake-up (moving: it already asks for frames; frozen: nothing to do).
 * @param {object|null} s
 * @param {object} [env] as catFps
 * @returns {number}
 */
export function catWakeAt(s, env = NO_ENV) {
  if (!s || frozen(env) || wantsFrames(s)) return Infinity;
  return s.until;
}

/**
 * Advances the cat. `now` is the wall clock (s): rests last real seconds even when no frame was
 * drawn during them; motion uses `dt` (capped).
 * @param {object} s
 * @param {number} dt seconds
 * @param {number} now seconds
 * @param {{hidden?:boolean, building?:boolean, riding?:boolean, reducedMotion?:boolean,
 *   avoid?:{x:number, z:number}|null}} [env] avoid: the floor's chicken, kept clear of
 * @returns {boolean} true when the cat needs the next frame
 */
export function stepCat(s, dt, now, env = NO_ENV) {
  if (!s) return false;
  if (env.reducedMotion) {
    holdStill(s, now);
    return false;
  }
  if (frozen(env)) return false;
  const d = clamp(Number(dt) || 0, 0, 0.2);
  if (s.mode === 'walk') walk(s, d, now, env);
  else if (s.mode === 'groom') groom(s, d, now, env);
  else if (s.mode === 'look') look(s, d, now, env);
  else if (s.mode === 'stretch') stretch(s, d, now, env);
  else rest(s, d, now, env);
  return wantsFrames(s);
}

/** Reduced motion: its still posture at once (sitting when it was up); it goes on when motion is back. */
function holdStill(s, now) {
  if (s.mode !== 'rest') {
    s.until = now;
    s.posture = s.posture === 'lie' ? 'lie' : 'sit';
  }
  s.mode = 'rest';
  s.posed = true;
  s.speed = 0;
  s.amp = 0;
  s.bob = 0;
  setJoints(s, CAT_POSES[s.posture]);
}

/** Moves every joint toward `pose` (exponential, frame-rate independent). */
function approach(s, pose, rate, d) {
  for (let i = 0; i < CAT_JOINTS.length; i++) {
    const k = CAT_JOINTS[i];
    s[k] = damp(s[k], pose[k], rate, d);
  }
}

/** True when every joint is within `e` of `pose` (the reach within e / 4). */
function near(s, pose, e) {
  for (let i = 0; i < CAT_JOINTS.length; i++) {
    const k = CAT_JOINTS[i];
    const tol = k === 'hipY' || k === 'hipZ' ? e * 0.25 : e;
    if (Math.abs(s[k] - pose[k]) > tol) return false;
  }
  return true;
}

function rest(s, d, now, env) {
  if (!s.posed) {
    const pose = CAT_POSES[s.posture];
    approach(s, pose, CAT.settleRate, d);
    s.bob = damp(s.bob, 0, CAT.settleRate * 2, d);
    s.amp = 0;
    if (near(s, pose, 0.004) && Math.abs(s.bob) < 1e-4) {
      setJoints(s, pose);
      s.bob = 0;
      s.posed = true;
    }
  }
  if (now >= s.until - CAT.wakeSlack) next(s, now, 'rest', env);
}

/** A still rest in `posture` (it settles into it first when it is not there yet). */
function startRest(s, now, posture, length = null) {
  s.mode = 'rest';
  s.posture = posture;
  s.speed = 0;
  let span = length;
  if (span === null) {
    if (posture === 'lie') span = lerp(CAT.napMin, CAT.napMax, s.rand());
    else if (posture === 'sit') span = lerp(CAT.sitMin, CAT.sitMax, s.rand());
    else span = lerp(CAT.standMin, CAT.standMax, s.rand());
  }
  s.until = now + span;
  s.posed = near(s, CAT_POSES[posture], 1e-6) && s.bob === 0;
}

/**
 * Picks a reachable target a few steps away near a team (ahead first, then anywhere; the last tries
 * accept any reachable point, so a cat that strayed comes back), whose way keeps clear of `avoid`:
 * never closer to it than CAT.avoid, or than the cat already is (then the way leads away from it).
 */
export function pickTarget(s, avoid = null) {
  const C = CAT;
  const shy = !!avoid && Number.isFinite(avoid.x) && Number.isFinite(avoid.z);
  const clearance = shy ? Math.min(C.avoid, Math.hypot(s.x - avoid.x, s.z - avoid.z)) - 1e-9 : 0;
  for (let i = 0; i < C.tries; i++) {
    const a = i < C.tries / 2 ? s.yaw + (s.rand() - 0.5) * 2.4 : s.rand() * TWO_PI;
    const dist = lerp(C.walkMin, C.walkMax, s.rand());
    const tx = s.x + Math.sin(a) * dist;
    const tz = s.z + Math.cos(a) * dist;
    if (!segmentClear(s.area, s.x, s.z, tx, tz)) continue;
    if (i < C.tries - 4 && !nearTeam(s.area, tx, tz)) continue;
    if (shy && segmentDistance(s.x, s.z, tx - s.x, tz - s.z, avoid.x, avoid.z) < clearance) continue;
    s.tx = tx;
    s.tz = tz;
    return true;
  }
  return false;
}

function startWalk(s, now, env) {
  if (!pickTarget(s, env.avoid || null)) return false;
  s.mode = 'walk';
  s.deadline = now + CAT.walkTimeout;
  return true;
}

function startGroom(s) {
  s.mode = 'groom';
  s.speed = 0;
  s.k = 0;
  s.dur = lerp(CAT.groomMin, CAT.groomMax, s.rand());
}

function startLook(s) {
  s.mode = 'look';
  s.speed = 0;
  s.k = 0;
  s.dur = lerp(CAT.lookMin, CAT.lookMax, s.rand());
  s.side = s.rand() < 0.5 ? -1 : 1;
}

function startStretch(s) {
  s.mode = 'stretch';
  s.speed = 0;
  s.k = 0;
  s.dur = CAT.stretchTime;
}

/** What comes after `from`: mostly a long rest, sitting or lying. */
function next(s, now, from, env) {
  const r = s.rand();
  const walkOr = (posture, length = null) => {
    if (!startWalk(s, now, env)) startRest(s, now, posture, length ?? CAT.retryRest);
  };
  if (from === 'rest') {
    if (s.posture === 'sit') {
      if (r < 0.38) walkOr('sit');
      else if (r < 0.6) startGroom(s);
      else if (r < 0.78) startLook(s);
      else startRest(s, now, 'lie');
    } else if (s.posture === 'lie') {
      if (r < 0.62) startStretch(s);
      else startRest(s, now, 'sit');
    } else if (r < 0.55) startRest(s, now, 'sit');
    else walkOr('stand');
  } else if (from === 'walk') {
    if (r < 0.55) startRest(s, now, 'sit');
    else if (r < 0.8) startRest(s, now, 'stand');
    else startRest(s, now, 'lie');
  } else if (from === 'stretch') {
    if (r < 0.7) walkOr('stand');
    else startRest(s, now, 'sit');
  } else if (r < 0.2 && from === 'look') startGroom(s);
  else startRest(s, now, 'sit', lerp(CAT.sitMin, CAT.sitMax, s.rand()) * 0.7);
}

function walk(s, d, now, env) {
  const C = CAT;
  const S = CAT_POSES.stand;
  const dx = s.tx - s.x;
  const dz = s.tz - s.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 0.02 || now > s.deadline) {
    next(s, now, 'walk', env);
    return;
  }
  // Up on its feet first (from sitting or lying), then it walks.
  const up = Math.abs(s.hipY) < 0.025 && Math.abs(s.pitch) < 0.12
    && Math.abs(s.fl - S.fl) < 0.35 && Math.abs(s.bl - S.bl) < 0.35;
  const rate = up ? 10 : C.settleRate * 1.4;
  s.hipY = damp(s.hipY, S.hipY, rate, d);
  s.hipZ = damp(s.hipZ, S.hipZ, rate, d);
  s.pitch = damp(s.pitch, S.pitch, rate, d);
  s.reach = damp(s.reach, S.reach, rate, d);
  s.headYaw = damp(s.headYaw, 0, 4, d);
  s.tailSway = damp(s.tailSway, 0, 4, d);
  s.tailWrap = damp(s.tailWrap, 0, 4, d);
  if (!up) {
    s.fl = damp(s.fl, S.fl, rate, d);
    s.fr = damp(s.fr, S.fr, rate, d);
    s.bl = damp(s.bl, S.bl, rate, d);
    s.br = damp(s.br, S.br, rate, d);
    s.headPitch = damp(s.headPitch, S.headPitch, rate, d);
    s.tailLift = damp(s.tailLift, S.tailLift, rate, d);
    s.tailCurl = damp(s.tailCurl, S.tailCurl, rate, d);
    s.tailRoll = damp(s.tailRoll, S.tailRoll, rate, d);
    return;
  }
  const heading = Math.atan2(dx, dz);
  s.yaw = wrapAngle(dampAngle(s.yaw, heading, C.turnRate, d));
  const off = Math.abs(wrapAngle(heading - s.yaw));
  // Turns on the spot first, then walks; slows down on arrival.
  const align = clamp(1 - off / 0.9, 0, 1);
  const brake = clamp(dist / 0.4, 0.3, 1);
  s.speed = damp(s.speed, C.speed * align * brake, 5, d);
  // Along the checked segment (not along the yaw): the path never leaves the free area.
  const step = Math.min(dist, s.speed * d);
  s.x += (dx / dist) * step;
  s.z += (dz / dist) * step;
  // Diagonal gait: front left with hind right, then the other pair; steps on the spot while turning.
  s.gait = (s.gait + (step / C.stride) * Math.PI + (1 - align) * d * 7) % TWO_PI;
  s.amp = damp(s.amp, clamp((s.speed / C.speed) * 1.2 + (1 - align), 0, 1), 8, d);
  const swing = Math.sin(s.gait) * 0.42 * s.amp;
  s.fl = swing;
  s.br = swing;
  s.fr = -swing;
  s.bl = -swing;
  s.bob = Math.abs(Math.cos(s.gait)) * 0.006 * s.amp;
  s.headPitch = damp(s.headPitch, S.headPitch + 0.06, 6, d) + Math.sin(s.gait * 2) * 0.004 * s.amp;
  // Tail up, its tip hooked forward, a slow sway.
  s.tailLift = damp(s.tailLift, 1.2, 5, d);
  s.tailCurl = damp(s.tailCurl, 0, 5, d);
  s.tailRoll = damp(s.tailRoll, -1.57, 5, d);
  s.tailSway = Math.sin(s.gait * 0.5) * 0.5 * s.amp;
  s.tailWrap = Math.sin(s.gait * 0.5 + 1.1) * 0.3 * s.amp;
}

/** Sitting, the right front paw raised to the mouth and licked, the head bobbing with it. */
function groom(s, d, now, env) {
  const P = CAT_POSES.sit;
  s.k += d;
  const p = s.k / s.dur;
  if (p >= 1) {
    next(s, now, 'groom', env);
    return;
  }
  // Up during the first fifth, down during the last one.
  const w = Math.min(1, p / 0.2, (1 - p) / 0.2);
  const lick = Math.sin(s.k * Math.PI * 2 * 2.4);
  Object.assign(aim, P);
  aim.fr = lerp(P.fr, GROOM.fr + lick * 0.05, w);
  aim.headPitch = lerp(P.headPitch, GROOM.headPitch + lick * 0.09, w);
  aim.headYaw = lerp(P.headYaw, GROOM.headYaw, w);
  aim.reach = lerp(P.reach, GROOM.reach, w);
  approach(s, aim, 12, d);
}

/** Sitting, it turns its head to one side, then the other, the tip of its tail twitching. */
function look(s, d, now, env) {
  const P = CAT_POSES.sit;
  s.k += d;
  const p = s.k / s.dur;
  if (p >= 1) {
    next(s, now, 'look', env);
    return;
  }
  Object.assign(aim, P);
  aim.headYaw = p < 0.45 ? s.side * 0.8 : p < 0.85 ? -s.side * 0.5 : 0;
  aim.headPitch = P.headPitch - 0.12;
  approach(s, aim, 5, d);
  // The tip of the tail twitches (set, not eased: it follows the wave).
  s.tailWrap = P.tailWrap + Math.sin(s.k * 5.5) * 0.22 * Math.min(1, p / 0.15, (1 - p) / 0.15);
}

/** Gets up from a nap with a long stretch: front low and paws forward, rear up, then on its feet. */
function stretch(s, d, now, env) {
  s.k += d;
  const p = s.k / s.dur;
  if (p >= 1) {
    next(s, now, 'stretch', env);
    return;
  }
  // Up and into the stretch, held, then back to standing.
  const w = p < 0.3 ? 0 : p < 0.5 ? (p - 0.3) / 0.2 : p < 0.75 ? 1 : Math.max(0, (0.95 - p) / 0.2);
  const S = CAT_POSES.stand;
  for (let i = 0; i < CAT_JOINTS.length; i++) {
    const k = CAT_JOINTS[i];
    s[k] = damp(s[k], lerp(S[k], STRETCH[k], w), p < 0.3 ? CAT.settleRate * 1.3 : 8, d);
  }
  s.bob = damp(s.bob, 0, 10, d);
}
